<template>
  <v-container fluid>
    <InspectionsTable class="pb-4">
      <template v-slot:actions="{ dataset }">
        <avs-button
          :data="dataset"
          color="secondary"
          iconI="mdi-export"
          :spantext="
            $t('actions.export', {
              entity: $tc('categories.inspection.name', 2).toLowerCase()
            })
          "
          text
          @click="exportToExcel()"
        >
          <template v-slot:name>
            {{ $t("actions.export_") }}
          </template>
        </avs-button>
        <avs-createButton
          menu
          :name="$t('commons.inspection_title')"
          :spantext="$t('commons.inspection_title')"
        >
          <template v-slot:list>
            <v-list>
              <v-list-item-group color="primary">
                <v-list-item
                  v-for="(item, i) in newMenuItems"
                  :key="i"
                  @click="item.function()"
                >
                  <v-list-item-icon>
                    <v-icon v-text="item.icon"></v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title v-text="item.text"></v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-item-group>
            </v-list>
          </template>
        </avs-createButton>
        <avs-createButton
          @click="handleNewInspectionForm"
          :spantext="$tc('categories.inspection.fill', 1).toLowerCase()"
          :name="$tc('categories.inspection.fill', 1).toLowerCase()"
        />
      </template>
    </InspectionsTable>
  </v-container>
</template>
<script>
import InspectionsTable from "@/components/ri3/inspection/InspectionsTable.vue"
import InspectionService from "@/services/InspectionService"
import { now, add, toDatetime } from "@/utils/datetime"
import ExcelJS from "exceljs"
import { saveAs } from "file-saver"
import { fmtInspectionData } from "@/utils/download"

export default {
  name: "InspectionsView",
  components: { InspectionsTable },
  computed: {
    project() {
      return this.$store.state.app.project
    }
  },
  data() {
    return {
      loading: false,
      newMenuItems: [
        {
          text: this.$t("categories.inspection.quick"),
          icon: "mdi-clipboard-text-clock-outline",
          function: this.handleNewMeeting
        },
        {
          text: this.$t("categories.inspection.plan"),
          icon: "mdi-calendar-cursor",
          function: this.handleNewInspection
        }
        // {
        //   text: this.$t("categories.inspection.fill"),
        //   icon: "mdi-file-document-edit-outline",
        //   function: this.handleNewInspectionForm
        // }
      ]
    }
  },
  methods: {
    createInspection(redirectPath, tab = null, notify = false) {
      this.loading = true
      const startDate = now()
      const endDate = add(now(), 1, "h")
      InspectionService.create(this, this.project.id, {
        name: "NONE",
        start_date: startDate,
        end_date: endDate,
        status: "Ongoing",
        notify_oncreated: notify,
        lang: this.$i18n.locale
      })
        .then(r => {
          this.$store.commit("app/message", {
            msg: this.$t("messages.success.created", {
              entity: this.$tc("categories.inspection.name", 1)
            }),
            color: "success"
          })
          this.$router.push({
            name: redirectPath,
            params: { id: r.id, uuid: r.uuid, tab: tab }
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleNewInspection() {
      this.$router.push({
        name: "InspectionCreateUpdate"
      })
    },
    handleNewInspectionForm() {
      this.createInspection("Inspection", 2)
    },
    handleNewMeeting() {
      this.createInspection("InspectionMeeting", null, false)
    },
    preFmtData(dataset) {
      return fmtInspectionData(dataset)
    },
    async exportToExcel() {
      // 1. Récupérer les données
      const resp = await InspectionService.get(this, this.project.id)
      const allData = resp.results
      if (!allData.length) {
        return this.$store.commit("app/message", {
          msg: this.$t("messages.warning.no_data_to_export"),
          color: "warning"
        })
      }

      // 2. Préparer titre, en‑têtes et lignes
      const rawTitle = this.$t("commons.drawer.inspections").toUpperCase()
      const sheetName = rawTitle.slice(0, 31)
      const headers = [
        this.$t("commons.project_name"),
        this.$t("commons.name"),
        this.$t("commons.date.start_date"),
        this.$t("commons.date.end_date"),
        this.$t("commons.status.name"),
        this.$t("categories.inspection.responsible"),
        this.$t("categories.notification.name")
      ]
      const exportData = allData.map(item => [
        item.project.name,
        item.name,
        toDatetime(item.start_date),
        toDatetime(item.end_date),
        item.status,
        item.inspector?.first_name
          ? `${item.inspector.first_name} ${item.inspector.last_name}`
          : "N/A",
        item.notify_team_oncreated ? this.$t("commons.yes") : this.$t("commons.no")
      ])

      // 3. Créer le workbook et la feuille
      const wb = new ExcelJS.Workbook()
      const ws = wb.addWorksheet(sheetName)

      // 4. Insérer titre (A1:F1), sauter une ligne, puis les en‑têtes
      ws.mergeCells("A1:F1")
      ws.getCell("A1").value = rawTitle
      ws.getCell("A1").alignment = { horizontal: "center", vertical: "middle" }
      ws.getRow(1).font = { bold: true, size: 14 }
      ws.addRow([]) // ligne 2 vide
      ws.addRow(headers) // ligne 3 : en‑têtes

      // 5. Insérer les données à partir de la ligne 4
      exportData.forEach(row => ws.addRow(row))

      // 6. Ajuster largeur des colonnes
      ws.columns.forEach((col, i) => {
        const maxLength = Math.max(
          headers[i].length,
          ...exportData.map(r => (r[i] ? r[i].toString().length : 0))
        )
        col.width = maxLength + 2
      })

      // 7. Appliquer des bordures fines noires à toutes les cellules du tableau
      ws.eachRow((row, rowIndex) => {
        row.eachCell(cell => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          }
        })
        // mettre en gras la ligne d'en‑têtes (3)
        if (rowIndex === 3) row.font = { bold: true }
      })

      // 8. Générer le buffer et lancer le téléchargement
      const buf = await wb.xlsx.writeBuffer()
      const blob = new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      })
      saveAs(blob, `${sheetName.toLowerCase()}_${new Date().toISOString().slice(0, 10)}.xlsx`)

      // 9. Message de succès
      this.$store.commit("app/message", {
        msg: this.$t("Fichier Excel exporté avec succès"),
        color: "success"
      })
    }
  }
}
</script>
